"use client";

import { useState } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import {
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Tabs,
  TabList,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  TableLink,
  Avatar,
  AvatarGroup,
  Tag,
  Button,
  EmptyState,
  ListFilterBar,
  ResultCount,
} from "@/ui";
import { CircleCheckIcon, UserIcon, UserRoundCheckIcon } from "lucide-react";
import AddIcon from "@mui/icons-material/Add";
import PageTitle from "@components/shared/Typography/PageTitle";
import FadeIn from "@components/shared/loading/FadeIn";
import DateTime from "@components/shared/DateTime";
import MoreMenu from "./parts/MoreMenu";
import TableSkeleton from "@components/shared/loading/TableSkeleton";
import Paging from "@components/shared/table/Paging";
import StatusChip from "./parts/StatusChip";
import useFloats from "@lib/floats/useFloats";
import useFloatNames from "@lib/floats/useFloatNames";
import useFloatRecipients from "@lib/floats/useFloatRecipients";
import useFloatCandidates from "@lib/floats/useFloatCandidates";
import useSearch from "@lib/shared/useSearch";
import useUrlListState, { useUrlSync } from "@lib/shared/useUrlListState";
import useDuplicateFloat from "@lib/floats/useDuplicateFloat";
import useDeleteFloat from "@lib/floats/useDeleteFloat";
import sorting from "@components/shared/sorting";
import isPlaceholderPhoto from "@lib/candidates/isPlaceholderPhoto";
import { FLOAT_STATUS, FLOAT_SORT_OPTIONS } from "@lib/enums";
import { useRouter } from "next/navigation";
import NextLink from "next/link";
import useRecruiterDetails from "@lib/user/useRecruiterDetails";

const FloatListContainer = styled(Box)(({ theme }) => ({
  padding: "40px 46px 40px 46px",

  [theme.breakpoints.down("md")]: {
    padding: "30px 20px",
  },
}));

const Results = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  columnGap: "4px",
  alignItems: "center",
  justifyItems: "center",
});

const Item = styled(Box)({
  fontSize: "12px",
});

const ItemLabel = styled(Box)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: "10px",
}));

const sentStatus = (float) => {
  const emails =
    float.contacts?.filter((contact) => contact.type === "email").length || 0;
  const links =
    float.contacts?.filter((contact) => contact.type === "link").length || 0;

  const emailStatus = emails === 1 ? "1 email" : `${emails} emails`;
  const linkStatus = links === 1 ? "1 link" : `${links} links`;

  return (
    <>
      {emailStatus}
      <br />
      {linkStatus}
    </>
  );
};

export default function Floats() {
  const router = useRouter();
  const { recruiter } = useRecruiterDetails();
  const hasTeamSharing = recruiter?.user?.has_team_sharing ?? false;

  const { initial, updateUrl } = useUrlListState({
    defaultSortProperty: FLOAT_SORT_OPTIONS.UPDATED_AT,
    defaultSortDirection: "desc",
  });

  const [page, setPage] = useState(initial.page);
  const [activeFilters, setActiveFilters] = useState(initial.filters);
  const { names } = useFloatNames();
  const { recipients } = useFloatRecipients();
  const { candidates } = useFloatCandidates();
  const { SearchField, search } = useSearch(setPage, {
    placeholder: "Search floats...",
    helperText: "Search by name, recipient, or candidate",
    submitButton: true,
    suggestions: [...names, ...recipients, ...candidates.map((c) => c.name)],
    initialValue: initial.search,
  });
  const [sortBy, setSortBy] = useState(initial.sortBy);

  useUrlSync(updateUrl, { page, sortBy, search, filters: activeFilters });

  const filterFields = [
    {
      key: "status",
      label: "Status",
      type: "multiselect",
      icon: <CircleCheckIcon size={14} />,
      options: Object.values(FLOAT_STATUS).map((s) => ({
        value: s,
        label: s.charAt(0).toUpperCase() + s.slice(1),
      })),
    },
    {
      key: "recipient",
      label: "Recipient",
      type: "multiselect",
      icon: <UserIcon size={14} />,
      searchable: true,
      options: recipients.map((r) => ({ value: r, label: r })),
    },
    {
      key: "candidate",
      label: "Candidate",
      type: "multiselect",
      icon: <UserRoundCheckIcon size={14} />,
      searchable: true,
      options: candidates.map((c) => ({
        value: c.name,
        label: c.name,
        icon: (
          <Avatar
            src={isPlaceholderPhoto(c.photo_url) ? undefined : c.photo_url}
            name={c.name}
            size="xs"
          />
        ),
      })),
    },
  ];

  const getFilterValues = (field) =>
    activeFilters.find((f) => f.field === field)?.values ?? [];

  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
    setPage(1);
  };

  const filteredStatuses = getFilterValues("status");
  const filteredRecipients = getFilterValues("recipient");
  const filteredCandidates = getFilterValues("candidate");

  const activeApiFilters = {
    ...(filteredStatuses.length > 0 ? { status: filteredStatuses.join("|") } : {}),
    ...(filteredRecipients.length > 0 ? { recipient: filteredRecipients.join("|") } : {}),
    ...(filteredCandidates.length > 0 ? { candidate: filteredCandidates.join("|") } : {}),
  };

  // My Floats only
  const { floats, lastPage, total, from, to, hasMore, isLoading } = useFloats(
    page,
    sortBy,
    search,
    activeApiFilters
  );

  const duplicate = useDuplicateFloat();
  const deleteFloat = useDeleteFloat();

  const { changeDirection } = sorting;

  const handleSortBy = (property) => {
    if (sortBy.property === property) {
      setSortBy({ property, direction: changeDirection(sortBy.direction) });
    } else {
      setSortBy({ property, direction: "asc" });
    }
  };

  const handleDuplicate = (floatId) => {
    duplicate(floatId);
    document
      .getElementById("createButton")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const handleDelete = (floatId) => {
    deleteFloat(floatId);
  };

  const handleTabChange = (value) => {
    if (value === "team") {
      router.push("/team/floats");
    }
  };

  return (
    <FadeIn isLoading={isLoading}>
      <FloatListContainer>
        <Stack
          sx={{ mb: 2 }}
          direction="row"
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <PageTitle>Floats</PageTitle>
          <Button
            id="createButton"
            variant="accent"
            endIcon={<AddIcon />}
            onClick={() => router.push("/floats/create")}
          >
            Create new float
          </Button>
        </Stack>
        <Box sx={{ mb: 6, width: "60%" }}>
          <Typography variant="body2">
            A Float is a message that introduces one or more candidates to a
            potential client to spark a new business conversation.
          </Typography>
        </Box>
        {hasTeamSharing ? (
          <Tabs value="mine" onChange={handleTabChange}>
            <TabList>
              <Tab value="mine">My floats</Tab>
              <Tab value="team">Team floats</Tab>
            </TabList>
          </Tabs>
        ) : (
          <Divider />
        )}
        <Box sx={{ my: 4 }}>
          <Box sx={{ width: '50%', mb: 2 }}>{SearchField}</Box>
          <ListFilterBar
            fields={filterFields}
            filters={activeFilters}
            onChange={handleFiltersChange}
          />
        </Box>
        {isLoading && <TableSkeleton />}
        {!isLoading && floats?.length === 0 && (
          <EmptyState
            icon="🔍"
            title="No floats found"
            description="Try adjusting your search or filters to find what you're looking for."
          />
        )}
        {!isLoading && floats?.length > 0 && (
          <>
            <ResultCount
              from={from}
              to={to}
              total={total}
              noun="float"
            />
            <Table hoverable aria-label="float list">
              <TableHead>
                <TableRow>
                  <TableHeader
                    sortable
                    sortDirection={sortBy.property === FLOAT_SORT_OPTIONS.NAME ? sortBy.direction : null}
                    onSort={() => handleSortBy(FLOAT_SORT_OPTIONS.NAME)}
                  >
                    Float name
                  </TableHeader>
                  <TableHeader>Candidates</TableHeader>
                  <TableHeader>Recipients</TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortBy.property === FLOAT_SORT_OPTIONS.SENT_DATE ? sortBy.direction : null}
                    onSort={() => handleSortBy(FLOAT_SORT_OPTIONS.SENT_DATE)}
                  >
                    Status
                  </TableHeader>
                  <TableHeader responsive>Interactions</TableHeader>
                  <TableHeader
                    responsive
                    sortable
                    sortDirection={sortBy.property === FLOAT_SORT_OPTIONS.UPDATED_AT ? sortBy.direction : null}
                    onSort={() => handleSortBy(FLOAT_SORT_OPTIONS.UPDATED_AT)}
                  >
                    Updated
                  </TableHeader>
                  <TableHeader></TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {floats.map((float) => (
                  <TableRow key={float.id}>
                    <TableCell>
                      <TableLink
                        component={NextLink}
                        href={float.status === FLOAT_STATUS.DRAFT ? `/floats/${float.uuid}/edit` : `/floats/${float.uuid}`}
                      >
                        {float.name}
                      </TableLink>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="flex-start">
                        <AvatarGroup spacing="small" max={4}>
                          {float.candidates?.map((candidate) => (
                            <Tooltip
                              key={candidate.id}
                              title={candidate.candidate_name || candidate.name}
                              placement="top"
                              arrow
                            >
                              <Avatar
                                src={isPlaceholderPhoto(candidate.avatar) ? undefined : candidate.avatar}
                                name={candidate.candidate_name || candidate.name}
                                size="sm"
                              />
                            </Tooltip>
                          ))}
                        </AvatarGroup>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {float.contacts?.length > 0 && (
                        <Tooltip
                          title={float.contacts
                            .map((contact) => contact.name)
                            .join(", ")}
                          placement="top"
                          arrow
                        >
                          <span>
                            <Tag variant="minimal">
                              {float.contacts.length === 1
                                ? "1 recipient"
                                : `${float.contacts.length} recipients`}
                            </Tag>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={sentStatus(float)}
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        <span>
                          <StatusChip status={float.status} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell responsive>
                      <Results>
                        <Item>{float.email_opens_count || 0}</Item>
                        <Item>{float.views_count || 0}</Item>
                        <Item>{float.interaction_count || 0}</Item>
                        <ItemLabel>Opens</ItemLabel>
                        <ItemLabel>Views</ItemLabel>
                        <ItemLabel>Clicks</ItemLabel>
                      </Results>
                    </TableCell>
                    <TableCell responsive>
                      <DateTime date={float.updated_at} />
                    </TableCell>
                    <MoreMenu
                      float={float}
                      onDuplicate={handleDuplicate}
                      onDelete={handleDelete}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Paging
              page={page}
              lastPage={lastPage}
              setPage={setPage}
              hasMore={hasMore}
            />
          </>
        )}
      </FloatListContainer>
    </FadeIn>
  );
}
